import React from "react";
import { TrustProfileProps } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import { trustTypeLabels } from "../entity-specific/entityProfileConfig";

export const TrustProfileComponent: React.FC<TrustProfileProps> = ({
  profile,
  onEdit,
}) => {
  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Trust Profile</h3>
        {onEdit && (
          <button
            onClick={onEdit}
            className="text-sm text-blue-300 hover:text-white font-medium transition-colors"
          >
            Edit
          </button>
        )}
      </div>

      <div className="p-6">
        {/* Key Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase">
              Basic Information
            </h4>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500">Trust Name</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {profile.trustName}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Trust Type</dt>
                <dd className="text-sm text-gray-900">
                  <span className="inline-block px-2 py-1 bg-gradient-to-r from-violet-100 to-purple-100 text-violet-800 rounded-full text-xs font-medium ring-1 ring-violet-200">
                    {trustTypeLabels[profile.trustType] || profile.trustType}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Trustee Type</dt>
                <dd className="text-sm text-gray-900 capitalize">
                  {profile.trusteeType}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`inline-block px-2 py-1 text-xs font-medium rounded-full shadow-sm ${
                      profile.status === "active"
                        ? "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800"
                        : profile.status === "vested"
                          ? "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800"
                          : "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800"
                    }`}
                  >
                    {profile.status}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Registration Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase">
              Registration
            </h4>
            <dl className="space-y-3">
              {profile.registration.abn && (
                <div>
                  <dt className="text-xs text-gray-500">ABN</dt>
                  <dd className="text-sm font-mono text-gray-900">
                    {profile.registration.abn}
                  </dd>
                </div>
              )}
              {profile.registration.tfn && (
                <div>
                  <dt className="text-xs text-gray-500">TFN</dt>
                  <dd className="text-sm font-mono text-gray-900">
                    {profile.registration.tfn}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500">Establishment Date</dt>
                <dd className="text-sm text-gray-900">
                  {formatDateShort(profile.establishmentDate)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Trust Deed Date</dt>
                <dd className="text-sm text-gray-900">
                  {formatDateShort(profile.registration.trustDeedDate)}
                </dd>
              </div>
              {profile.registration.trustDeedAmendmentDate && (
                <div>
                  <dt className="text-xs text-gray-500">Last Amendment</dt>
                  <dd className="text-sm text-gray-900">
                    {formatDateShort(
                      profile.registration.trustDeedAmendmentDate,
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Vesting Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase">
              Vesting Information
            </h4>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500">Vesting Date</dt>
                <dd className="text-sm text-gray-900">
                  {formatDateShort(profile.registration.vestingDate)}
                </dd>
              </div>
              {profile.settlorDetails && (
                <div>
                  <dt className="text-xs text-gray-500">Settlor Details</dt>
                  <dd className="text-sm text-gray-900">
                    {profile.settlorDetails}
                  </dd>
                </div>
              )}
              {profile.appointerDetails && (
                <div>
                  <dt className="text-xs text-gray-500">Appointer Details</dt>
                  <dd className="text-sm text-gray-900">
                    {profile.appointerDetails}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Business Address */}
        {profile.businessAddress && (
          <div className="mt-6 p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/40">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Business Address
            </h4>
            <address className="text-sm text-gray-600 not-italic">
              {profile.businessAddress.line1}
              <br />
              {profile.businessAddress.line2 && (
                <>
                  {profile.businessAddress.line2}
                  <br />
                </>
              )}
              {profile.businessAddress.suburb}, {profile.businessAddress.state}{" "}
              {profile.businessAddress.postcode}
              <br />
              {profile.businessAddress.country}
            </address>
          </div>
        )}

        {/* Contact Details */}
        {profile.contactDetails && (
          <div className="mt-4 p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/40">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Contact Details
            </h4>
            <div className="flex flex-wrap gap-6 text-sm">
              {profile.contactDetails.email && (
                <div>
                  <span className="text-gray-500">Email:</span>
                  <a
                    href={`mailto:${profile.contactDetails.email}`}
                    className="ml-2 text-blue-600 hover:underline"
                  >
                    {profile.contactDetails.email}
                  </a>
                </div>
              )}
              {profile.contactDetails.phone && (
                <div>
                  <span className="text-gray-500">Phone:</span>
                  <a
                    href={`tel:${profile.contactDetails.phone}`}
                    className="ml-2 text-blue-600 hover:underline"
                  >
                    {profile.contactDetails.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustProfileComponent;
